import Vue from "vue";
import {
  fetchMessages,
  fetchSummary,
  fetchUnread,
  fetchViewAnalysis,
  getOrCreateInternalNotes,
  getOrCreateStudyDiscussion,
  markRead,
  requestSummary,
  requestViewAnalysis,
  sendMessage
} from "@/discussions/api/http";
import {
  closeDiscussionWebsocket,
  openDiscussionWebsocket
} from "@/discussions/api/websocket";
import { openDiscussionEvents } from "@/discussions/api/events";

const getInitialState = () => ({
  conversationsByStudy: {},
  internalConversationsByStudy: {},
  messagesByConversation: {},
  unreadByStudy: {},
  // Id of the first unread message captured when the panel opens, so the
  // "New messages" separator survives markLatestRead and polling reloads.
  unreadBoundaryByConversation: {},
  loading: false,
  sending: false,
  summarizing: false,
  analyzingView: false,
  // Last AI summary message anchor per conversation, used to flag the summary
  // as outdated when newer messages arrive: { messageId, createdAt }.
  lastSummaryByConversation: {},
  error: null,
  pollingTimer: null,
  realtimeConversationId: null,
  globalEventsSubscription: null
});

const upsertMessages = (state, conversationId, messages) => {
  const current = state.messagesByConversation[conversationId] || [];
  const byId = current.reduce((acc, message) => {
    const key = message.id ? `id:${message.id}` : `client:${message.client_msg_id}`;
    acc[key] = message;
    return acc;
  }, {});
  messages.forEach(message => {
    const idKey = message.id ? `id:${message.id}` : null;
    const clientKey = message.client_msg_id ? `client:${message.client_msg_id}` : null;
    if (clientKey) {
      delete byId[clientKey];
    }
    byId[idKey || clientKey] = message;
  });
  Vue.set(
    state.messagesByConversation,
    conversationId,
    Object.values(byId).sort((a, b) => {
      if (a.id && b.id) {
        return a.id - b.id;
      }
      return new Date(a.created_at) - new Date(b.created_at);
    })
  );
};

const newClientMsgId = () =>
  "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  );

export default {
  namespaced: true,
  state: getInitialState(),
  getters: {
    conversationForStudy: state => studyId => state.conversationsByStudy[studyId],
    internalConversationForStudy: state => studyId =>
      state.internalConversationsByStudy[studyId],
    messagesForConversation: state => conversationId =>
      state.messagesByConversation[conversationId] || [],
    unreadForStudy: state => studyId => state.unreadByStudy[studyId] || 0,
    unreadBoundaryForConversation: state => conversationId =>
      state.unreadBoundaryByConversation[conversationId] || null,
    lastSummaryForConversation: state => conversationId =>
      state.lastSummaryByConversation[conversationId] || null
  },
  mutations: {
    reset: state => {
      if (state.pollingTimer) {
        clearInterval(state.pollingTimer);
      }
      if (state.globalEventsSubscription) {
        state.globalEventsSubscription.close();
      }
      const next = getInitialState();
      Object.keys(next).forEach(key => Vue.set(state, key, next[key]));
    },
    setLoading: (state, value) => Vue.set(state, "loading", !!value),
    setSending: (state, value) => Vue.set(state, "sending", !!value),
    setSummarizing: (state, value) => Vue.set(state, "summarizing", !!value),
    setAnalyzingView: (state, value) => Vue.set(state, "analyzingView", !!value),
    setLastSummary: (state, { conversationId, summary }) => {
      Vue.set(state.lastSummaryByConversation, conversationId, summary || null);
    },
    setError: (state, error) => Vue.set(state, "error", error || null),
    setConversation: (state, conversation) => {
      Vue.set(state.conversationsByStudy, conversation.study, conversation);
    },
    setInternalConversation: (state, conversation) => {
      Vue.set(state.internalConversationsByStudy, conversation.study, conversation);
    },
    setMessages: (state, { conversationId, messages }) => {
      upsertMessages(state, conversationId, messages || []);
    },
    addOptimisticMessage: (state, { conversationId, message }) => {
      upsertMessages(state, conversationId, [message]);
    },
    replaceOptimisticMessage: (state, { conversationId, clientMsgId, message }) => {
      const current = state.messagesByConversation[conversationId] || [];
      Vue.set(
        state.messagesByConversation,
        conversationId,
        current
          .filter(item => item.client_msg_id !== clientMsgId)
          .filter(item => !message.id || item.id !== message.id)
          .concat([message])
          .sort((a, b) => {
            if (a.id && b.id) {
              return a.id - b.id;
            }
            return new Date(a.created_at) - new Date(b.created_at);
          })
      );
    },
    removeOptimisticMessage: (state, { conversationId, clientMsgId }) => {
      const current = state.messagesByConversation[conversationId] || [];
      Vue.set(
        state.messagesByConversation,
        conversationId,
        current.filter(item => item.id || item.client_msg_id !== clientMsgId)
      );
    },
    setUnread: (state, { studyId, unread }) => {
      Vue.set(state.unreadByStudy, studyId, unread || 0);
    },
    setUnreadBoundary: (state, { conversationId, messageId }) => {
      Vue.set(state.unreadBoundaryByConversation, conversationId, messageId || null);
    },
    setPollingTimer: (state, timer) => {
      if (state.pollingTimer) {
        clearInterval(state.pollingTimer);
      }
      Vue.set(state, "pollingTimer", timer);
    },
    setRealtimeConversation: (state, conversationId) => {
      Vue.set(state, "realtimeConversationId", conversationId || null);
    },
    setGlobalEventsSubscription: (state, subscription) => {
      if (state.globalEventsSubscription) {
        state.globalEventsSubscription.close();
      }
      Vue.set(state, "globalEventsSubscription", subscription || null);
    }
  },
  actions: {
    async openStudyDiscussion({ commit, dispatch }, studyId) {
      if (!studyId) {
        return null;
      }
      commit("setLoading", true);
      commit("setError", null);
      try {
        const conversation = await getOrCreateStudyDiscussion(studyId);
        commit("setConversation", conversation);
        await dispatch("loadMessages", conversation.id);
        return conversation;
      } catch (error) {
        commit("setError", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },
    async openInternalNotes({ commit, dispatch }, studyId) {
      if (!studyId) {
        return null;
      }
      commit("setLoading", true);
      commit("setError", null);
      try {
        const conversation = await getOrCreateInternalNotes(studyId);
        commit("setInternalConversation", conversation);
        await dispatch("loadMessages", conversation.id);
        return conversation;
      } catch (error) {
        commit("setError", error);
        throw error;
      } finally {
        commit("setLoading", false);
      }
    },
    async loadMessages({ commit }, conversationId) {
      if (!conversationId) {
        return;
      }
      const response = await fetchMessages(conversationId);
      commit("setMessages", {
        conversationId,
        messages: response.results || []
      });
    },
    startPolling({ commit, dispatch }, conversationId) {
      if (!conversationId) {
        return;
      }
      const timer = setInterval(() => dispatch("loadMessages", conversationId), 15000);
      commit("setPollingTimer", timer);
    },
    stopPolling({ commit }) {
      commit("setPollingTimer", null);
    },
    async startRealtime({ commit, dispatch, state }, conversationId) {
      if (!conversationId) {
        return false;
      }
      if (
        state.realtimeConversationId &&
        state.realtimeConversationId !== conversationId
      ) {
        closeDiscussionWebsocket(state.realtimeConversationId);
      }
      commit("setRealtimeConversation", conversationId);
      const connected = await openDiscussionWebsocket(conversationId, {
        onMessage: payload => dispatch("handleRealtimeEvent", payload),
        onClose: () => dispatch("startPolling", conversationId),
        onError: () => dispatch("startPolling", conversationId)
      });
      if (connected) {
        dispatch("stopPolling");
      } else {
        dispatch("startPolling", conversationId);
      }
      return connected;
    },
    stopRealtime({ commit, state }) {
      if (state.realtimeConversationId) {
        closeDiscussionWebsocket(state.realtimeConversationId);
      }
      commit("setRealtimeConversation", null);
    },
    startGlobalEvents({ commit, dispatch }, studyIds) {
      const ids = Array.isArray(studyIds) ? studyIds : [studyIds];
      const subscription = openDiscussionEvents(ids, {
        onSnapshot: payload => dispatch("handleGlobalSnapshot", payload),
        onUnreadChanged: payload => dispatch("handleGlobalUnreadChanged", payload),
        onError: () => ids.forEach(studyId => dispatch("refreshUnread", studyId))
      });
      commit("setGlobalEventsSubscription", subscription);
    },
    stopGlobalEvents({ commit }) {
      commit("setGlobalEventsSubscription", null);
    },
    handleGlobalSnapshot({ commit }, payload) {
      (payload?.studies || []).forEach(item => {
        commit("setUnread", { studyId: item.study, unread: item.unread });
      });
    },
    handleGlobalUnreadChanged({ commit, state, rootState }, payload) {
      if (!payload?.study) {
        return;
      }
      if (
        rootState.viewer?.discussionPanelOpen &&
        payload.conversation &&
        String(payload.conversation) === String(state.realtimeConversationId)
      ) {
        return;
      }
      commit("setUnread", { studyId: payload.study, unread: payload.unread });
    },
    handleRealtimeEvent({ commit, dispatch, state, rootState }, payload) {
      if (!payload?.message) {
        return;
      }
      if (payload.type === "message.updated") {
        commit("setMessages", {
          conversationId: payload.message.conversation,
          messages: [payload.message]
        });
        return;
      }
      if (payload.type !== "message.created") {
        return;
      }
      const message = payload.message;
      const currentUser = rootState.auth?.user || {};
      const isOwnMessage =
        (message.author_type === "user" &&
          String(message.author_user) === String(currentUser.id)) ||
        (message.author_type === "shared_guest" &&
          message.author_email &&
          message.author_email === currentUser.email);
      commit("setMessages", {
        conversationId: message.conversation,
        messages: [message]
      });
      const conversation = Object.values(state.conversationsByStudy).find(
        item => item?.id === message.conversation
      );
      if (!conversation?.study) {
        return;
      }
      if (rootState.viewer?.discussionPanelOpen) {
        if (!isOwnMessage) {
          if (!state.unreadBoundaryByConversation[message.conversation]) {
            commit("setUnreadBoundary", {
              conversationId: message.conversation,
              messageId: message.id
            });
          }
          commit("setUnread", {
            studyId: conversation.study,
            unread: (state.unreadByStudy[conversation.study] || 0) + 1
          });
        }
        dispatch("markLatestRead", message.conversation);
      } else {
        dispatch("refreshUnread", conversation.study);
      }
    },
    async send({ commit }, { conversationId, body, anchor, anchors }) {
      const clientMsgId = newClientMsgId();
      const optimistic = {
        id: null,
        conversation: conversationId,
        body,
        anchor: anchor || (anchors && anchors[0]) || null,
        anchors: anchors || (anchor ? [anchor] : []),
        client_msg_id: clientMsgId,
        created_at: new Date().toISOString(),
        pending: true
      };
      commit("addOptimisticMessage", { conversationId, message: optimistic });
      commit("setSending", true);
      try {
        const message = await sendMessage(conversationId, {
          body,
          anchors: anchors || (anchor ? [anchor] : []),
          client_msg_id: clientMsgId
        });
        commit("replaceOptimisticMessage", {
          conversationId,
          clientMsgId,
          message
        });
        return message;
      } catch (error) {
        // Roll back the optimistic message so it doesn't stay stuck as pending.
        commit("removeOptimisticMessage", { conversationId, clientMsgId });
        throw error;
      } finally {
        commit("setSending", false);
      }
    },
    async generateSummary({ commit, dispatch, getters }, { conversationId, mode }) {
      if (!conversationId) {
        return null;
      }
      commit("setSummarizing", true);
      try {
        const job = await requestSummary(conversationId, mode ? { mode } : {});
        // Poll until the async job finishes (the AI message also arrives via
        // realtime, but polling guarantees we resolve without a live socket).
        const deadline = Date.now() + 90000;
        let current = job;
        while (
          current &&
          (current.status === "queued" || current.status === "running") &&
          Date.now() < deadline
        ) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          current = await fetchSummary(job.id);
        }
        if (!current || current.status !== "ready") {
          throw new Error(
            current?.error_message || "Summary generation failed."
          );
        }
        await dispatch("loadMessages", conversationId);
        const messages = getters.messagesForConversation(conversationId);
        const summaryMessage = messages.find(
          item => item.id === current.output_message
        );
        commit("setLastSummary", {
          conversationId,
          summary: {
            messageId: current.output_message,
            createdAt: summaryMessage?.created_at || new Date().toISOString()
          }
        });
        await dispatch("markLatestRead", conversationId);
        return current;
      } finally {
        commit("setSummarizing", false);
      }
    },
    // "Ask AI about this view": post the user message (with the captured current
    // view as an anchor), then trigger the vision analysis and wait for the
    // async job to finish. The AI response also arrives via realtime, but
    // polling guarantees we resolve without a live socket.
    async askAiAboutView({ commit, dispatch }, { conversationId, body, anchors }) {
      if (!conversationId) {
        return null;
      }
      commit("setAnalyzingView", true);
      try {
        const requestMessage = await dispatch("send", {
          conversationId,
          body,
          anchors
        });
        if (!requestMessage || !requestMessage.id) {
          throw new Error("Could not attach the current view.");
        }
        const job = await requestViewAnalysis(conversationId, {
          request_message_id: requestMessage.id
        });
        const deadline = Date.now() + 90000;
        let current = job;
        while (
          current &&
          (current.status === "queued" || current.status === "running") &&
          Date.now() < deadline
        ) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          current = await fetchViewAnalysis(job.id);
        }
        if (!current || current.status !== "ready") {
          throw new Error(current?.error_message || "View analysis failed.");
        }
        await dispatch("loadMessages", conversationId);
        await dispatch("markLatestRead", conversationId);
        return current;
      } finally {
        commit("setAnalyzingView", false);
      }
    },
    // Capture the unread boundary (first unread message id) before marking as
    // read. With N unread and the latest messages loaded, the first unread is
    // the N-th message from the end.
    captureUnreadBoundary({ commit, state, getters }, { conversationId, studyId }) {
      const unread = state.unreadByStudy[studyId] || 0;
      const messages = getters.messagesForConversation(conversationId);
      const boundaryId =
        unread > 0 && messages.length >= unread
          ? messages[messages.length - unread].id
          : null;
      commit("setUnreadBoundary", { conversationId, messageId: boundaryId });
    },
    async markLatestRead({ getters }, conversationId) {
      const messages = getters.messagesForConversation(conversationId);
      const last = messages[messages.length - 1];
      if (last && last.id) {
        await markRead(conversationId, last.id);
      }
    },
    async refreshUnread({ commit }, studyId) {
      if (!studyId) {
        return;
      }
      const response = await fetchUnread(studyId);
      commit("setUnread", { studyId, unread: response.unread });
    }
  }
};
